import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { BookOpen, Clock, Eye, RotateCcw, Calendar, X, Filter, ArrowLeft, Loader2 } from 'lucide-react';
import { supabase, getSupabaseUrl } from '../supabase/client';
import { formatDateTime } from '../utils/timeUtils';
import { checkExamPermission } from '../utils/permissionCheck';
import PackagePurchaseModal from '../components/PackagePurchaseModal';

interface ExamRecord {
  id: string;
  paper_id: string;
  paper_title: string;
  score: number;
  total_score: number;
  time_left: number;
  exam_duration: number;
  submit_count: number;
  is_passed: boolean;
  created_at: string;
  answers?: Record<string, string>;
  questions?: any[];
  question_scores?: Record<string, number>;
  question_feedbacks?: Record<string, { score?: number; feedback?: string }> | null;
  objectiveScore?: number | null;
  objective_score?: number | null;
  objective_total_score?: number | null;
  objectiveTotalScore?: number | null;
  programmingScore?: number | null;
  programming_score?: number | null;
  source?: 'practice' | 'class' | 'exam';
  sourceLabel?: string;
  paper_exists?: boolean;
}

interface ExamRecordsPageProps {
  records?: ExamRecord[];
  stats?: {
    totalCount: number;
    passCount: number;
    avgAccuracy: number;
    bestScore: number;
  };
  isAdminView?: boolean;
  targetUserName?: string;
  targetUserId?: string;
}

export default function ExamRecordsPage({
  records: injectedRecords,
  stats: injectedStats,
  isAdminView: injectedIsAdminView,
  targetUserName: injectedTargetUserName,
  targetUserId: injectedTargetUserId
}: ExamRecordsPageProps = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const urlStudentId = searchParams.get('studentId');
  const returnUrl = searchParams.get('returnUrl');
  const targetUserId = injectedTargetUserId || urlStudentId;
  const [isAdminView, setIsAdminView] = useState(injectedIsAdminView || !!injectedTargetUserId || false);
  const [loading, setLoading] = useState(!injectedRecords);

  const [records, setRecords] = useState<ExamRecord[]>(injectedRecords || []);
  const [stats, setStats] = useState(injectedStats || {
    totalCount: 0,
    passCount: 0,
    avgAccuracy: 0,
    bestScore: 0,
  });
  const [selectedRecord, setSelectedRecord] = useState<ExamRecord | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [timeFilter, setTimeFilter] = useState<'7days' | '15days' | '30days'>('30days');
  const [filteredRecords, setFilteredRecords] = useState<ExamRecord[]>([]);
  const [targetUserName, setTargetUserName] = useState<string>(injectedTargetUserName || '');
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [selectedPaperForPurchase, setSelectedPaperForPurchase] = useState<any>(null);
  const [checkingPermission, setCheckingPermission] = useState<string | null>(null);
  const [sourceFilter, setSourceFilter] = useState<'all' | 'class' | 'exam' | 'practice'>('all');

  useEffect(() => {
    if (injectedRecords !== undefined) {
      setRecords(injectedRecords);
      setFilteredRecords(injectedRecords);
      if (injectedStats) {
        setStats(injectedStats);
      }
      setIsAdminView(true);
      if (injectedTargetUserName) {
        setTargetUserName(injectedTargetUserName);
      }
      setLoading(false);
      return;
    }
    loadRecords();
  }, [injectedRecords, injectedStats, injectedTargetUserName]);

  // 监听清除刷题记录事件，自动刷新数据
  useEffect(() => {
    const handleRecordsCleared = (e: Event) => {
      const detail = (e as CustomEvent).detail as { studentId: string };
      // 如果当前页面正在查看该学员的记录，则刷新
      if (targetUserId && detail.studentId === targetUserId) {
        loadRecords();
      }
    };
    window.addEventListener('student-records-cleared', handleRecordsCleared);
    return () => {
      window.removeEventListener('student-records-cleared', handleRecordsCleared);
    };
  }, [targetUserId]);

  useEffect(() => {
    if (!injectedRecords) {
      filterRecords();
    }
  }, [records, timeFilter, sourceFilter, injectedRecords]);

  const loadRecords = async () => {
    setLoading(true);

    try {
      // 当有 studentId 参数时（管理员/教师代查），不需要验证学员登录
      const isProxyView = !!urlStudentId;

      let accessToken = '';
      if (!isProxyView) {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) {
          navigate('/login', { replace: true });
          return;
        }
        accessToken = session.access_token;
      }

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
      }

      const response = await fetch(`${getSupabaseUrl()}/functions/v1/exam-service`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ action: 'get_records', studentId: targetUserId }),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        if (!isProxyView && response.status === 401) {
          navigate('/login', { replace: true });
        } else {
          // 管理员/教师代查模式下，接口错误不跳转登录页，直接显示空数据
          setRecords([]);
          setStats({ totalCount: 0, passCount: 0, avgAccuracy: 0, bestScore: 0 });
        }
        return;
      }

      setRecords(result.records || []);
      setStats(result.stats || { totalCount: 0, passCount: 0, avgAccuracy: 0, bestScore: 0 });
      setIsAdminView(isProxyView || result.isAdminView || false);
      setTargetUserName(result.targetUserName || '');
    } catch (error) {
      if (!urlStudentId) {
        navigate('/exam-records', { replace: true });
      } else {
        setRecords([]);
        setStats({ totalCount: 0, passCount: 0, avgAccuracy: 0, bestScore: 0 });
      }
    } finally {
      setLoading(false);
    }
  };

  const filterRecords = () => {
    let filtered = [...records];

    // 按来源筛选
    if (sourceFilter !== 'all') {
      filtered = filtered.filter(r => r.source === sourceFilter);
    }

    // 按时间筛选（仅非管理员视图）
    if (!isAdminView) {
      if (timeFilter === '7days') {
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        filtered = filtered.filter(r => new Date(r.created_at) >= sevenDaysAgo);
      } else if (timeFilter === '15days') {
        const fifteenDaysAgo = new Date();
        fifteenDaysAgo.setDate(fifteenDaysAgo.getDate() - 15);
        filtered = filtered.filter(r => new Date(r.created_at) >= fifteenDaysAgo);
      } else if (timeFilter === '30days') {
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        filtered = filtered.filter(r => new Date(r.created_at) >= thirtyDaysAgo);
      }
    }

    setFilteredRecords(filtered);
  };

  const formatDuration = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    if (m > 0) {
      return `${m}分${s.toString().padStart(2, '0')}秒`;
    }
    return `${s}秒`;
  };

  const getCorrectRate = (score: number, total: number) => {
    return total > 0 ? Math.round((score / total) * 100) : 0;
  };

  // 计算客观题正确率：直接使用数据库中已保存的 objective_score 和 objective_total_score
  const getObjectiveCorrectRate = (record: ExamRecord) => {
    const objectiveScore = record.objective_score ?? record.objectiveScore ?? 0;
    const objectiveTotalScore = record.objective_total_score ?? 0;

    if (objectiveTotalScore === 0) {
      // 如果数据库中没有 objective_total_score，尝试从题目数据中计算总分
      if (!record.questions || record.questions.length === 0) return null;
      const objectiveTypes = ['single_choice', 'multi_choice', 'judge'];
      const objectiveQuestions = record.questions.filter((q: any) => objectiveTypes.includes(q.type));
      if (objectiveQuestions.length === 0) return null;
      const totalFromQuestions = objectiveQuestions.reduce((total: number, q: any) => total + (q.score || 0), 0);
      if (totalFromQuestions === 0) return null;
      return Math.round((objectiveScore / totalFromQuestions) * 100);
    }

    return Math.round((objectiveScore / objectiveTotalScore) * 100);
  };

  const getAccuracyColor = (rate: number) => {
    if (rate >= 80) return 'text-green-600';
    if (rate >= 60) return 'text-amber-600';
    return 'text-red-600';
  };

  const getAccuracyBg = (rate: number) => {
    if (rate >= 80) return 'bg-green-100 text-green-700';
    if (rate >= 60) return 'bg-amber-100 text-amber-700';
    return 'bg-red-100 text-red-700';
  };

  const viewDetail = async (record: ExamRecord) => {
    // 跳转到 Quiz 页面作答详情，与考试结果页保持一致
    // 代查模式（管理员/教师查看学员记录）时传递 isProxyView 标记，跳过登录验证
    const isProxyView = !!urlStudentId;
    navigate(`/quiz/${record.paper_id}`, {
      state: {
        fromResult: true,
        isProxyView,
        resultData: {
          paperId: record.paper_id,
          paperTitle: record.paper_title,
          score: record.score,
          totalScore: record.total_score,
          timeLeft: record.time_left,
          examDuration: record.exam_duration,
          answers: record.answers || {},
          questions: record.questions || [],
          objectiveScore: record.objectiveScore ?? record.objective_score,
          objectiveTotalScore: record.objectiveTotalScore ?? record.objective_total_score,
          programmingScore: record.programmingScore ?? record.programming_score,
          questionFeedbacks: record.question_feedbacks || null
        }
      }
    });
  };

  const handleRetake = async (record: ExamRecord) => {
    if (record.paper_id === 'custom') {
      navigate('/exam-platform');
      return;
    }

    // 检查原卷是否被删除（所有来源统一检查）
    if (!record.paper_exists) {
      alert('后台原卷已删除，无法作答！');
      return;
    }

    // 课堂作业/考级竞赛来源的记录，需要进一步判断试卷类型
    if (record.source === 'class' || record.source === 'exam') {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        navigate('/login', { replace: true });
        return;
      }

      // 先查教师自有试卷表
      const { data: teacherPaper } = await supabase
        .from('teacher_papers')
        .select('id')
        .eq('id', record.paper_id)
        .maybeSingle();

      if (teacherPaper) {
        // 教师自有试卷，直接跳转
        navigate(`/quiz/${record.paper_id}`);
        return;
      }

      // 查刷题平台试卷表
      const { data: paperData } = await supabase
        .from('papers')
        .select('organization, category_type, level_text, is_free')
        .eq('id', record.paper_id)
        .maybeSingle();

      if (!paperData) {
        alert('试卷不存在，无法作答！');
        return;
      }

      // 免费试卷直接跳转
      if (paperData.is_free) {
        navigate(`/quiz/${record.paper_id}`);
        return;
      }

      // 付费试卷，检查刷题包权限（复用现有逻辑）
      setCheckingPermission(record.id);
      try {
        const permResult = await checkExamPermission(
          session.user.id,
          paperData.organization || '',
          paperData.category_type || '',
          paperData.level_text || ''
        );

        if (permResult.hasPermission) {
          navigate(`/quiz/${record.paper_id}`);
        } else {
          setSelectedPaperForPurchase({
            id: record.paper_id,
            title: record.paper_title,
            organization: paperData.organization || '',
            category_type: paperData.category_type || '',
            level_text: paperData.level_text || '',
          });
          setShowPurchaseModal(true);
        }
      } catch {
        navigate(`/quiz/${record.paper_id}`);
      } finally {
        setCheckingPermission(null);
      }
      return;
    }

    // 刷题来源的记录，需要检查刷题包权限
    setCheckingPermission(record.id);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        navigate('/login', { replace: true });
        return;
      }

      // 从试卷表获取级别信息
      const { data: paperData } = await supabase
        .from('papers')
        .select('organization, category_type, level_text, is_free')
        .eq('id', record.paper_id)
        .maybeSingle();

      if (!paperData) {
        // 试卷不存在，直接跳转
        navigate(`/quiz/${record.paper_id}`);
        return;
      }

      // 免费试卷直接跳转
      if (paperData.is_free) {
        navigate(`/quiz/${record.paper_id}`);
        return;
      }

      // 检查刷题包权限
      const permResult = await checkExamPermission(
        session.user.id,
        paperData.organization || '',
        paperData.category_type || '',
        paperData.level_text || ''
      );

      if (permResult.hasPermission) {
        // 有权限，直接跳转答题
        navigate(`/quiz/${record.paper_id}`);
      } else {
        // 无权限，弹出购买弹窗
        setSelectedPaperForPurchase({
          id: record.paper_id,
          title: record.paper_title,
          organization: paperData.organization || '',
          category_type: paperData.category_type || '',
          level_text: paperData.level_text || '',
        });
        setShowPurchaseModal(true);
      }
    } catch {
      // 权限检查失败，直接跳转（降级处理）
      navigate(`/quiz/${record.paper_id}`);
    } finally {
      setCheckingPermission(null);
    }
  };

  const getSubmitCountText = (count: number) => {
    return `第${count}次作答`;
  };

  // 获取来源标签样式 - 使用更醒目的设计
  const getSourceBadgeStyle = (source?: string) => {
    switch (source) {
      case 'practice':
        // 刷题 - 蓝色渐变，带阴影
        return 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-md';
      case 'class':
        // 课堂作业 - 绿色渐变，带阴影
        return 'bg-gradient-to-r from-emerald-500 to-emerald-600 text-white shadow-md';
      case 'exam':
        // 考级竞赛 - 紫色渐变，带阴影
        return 'bg-gradient-to-r from-violet-500 to-purple-600 text-white shadow-md';
      default:
        return 'bg-gradient-to-r from-gray-500 to-gray-600 text-white shadow-md';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center justify-between mb-6">
          <div>
            {isAdminView ? (
              <>
                <h1 className="text-2xl font-bold text-gray-800">学员答题记录</h1>
                <p className="text-sm text-gray-500 mt-1">学员：{targetUserName || targetUserId}</p>
              </>
            ) : (
              <>
                <h1 className="text-2xl font-bold text-gray-800">答题记录</h1>
                <p className="text-sm text-gray-500 mt-1">查看您的答题历史和成绩分析</p>
              </>
            )}
          </div>
          <div className="flex items-center gap-3">
            {isAdminView && (
              <button
                onClick={() => {
                  // 优先使用 returnUrl 参数，否则根据路径判断
                  if (returnUrl) {
                    navigate(returnUrl);
                  } else {
                    const isTeacherView = location.pathname.startsWith('/teacher');
                    navigate(isTeacherView ? '/teacher/students' : '/admin/members');
                  }
                }}
                className="flex items-center gap-1 px-4 py-2 text-base font-semibold text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors shadow-sm"
              >
                <ArrowLeft className="w-4 h-4" />
                返回学员列表
              </button>
            )}
            {!isAdminView && (
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-gray-500" />
                <select
                  value={timeFilter}
                  onChange={(e) => setTimeFilter(e.target.value as '7days' | '15days' | '30days')}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                >
                  <option value="7days">近7天</option>
                  <option value="15days">近15天</option>
                  <option value="30days">近30天</option>
                </select>
              </div>
            )}
          </div>
        </div>

        {/* 来源筛选按钮 */}
        {!isAdminView && (
          <div className="flex items-center gap-2 mb-4">
            {[
              { value: 'all', label: '全部' },
              { value: 'class', label: '课堂作业' },
              { value: 'exam', label: '考级竞赛' },
              { value: 'practice', label: '刷题平台' }
            ].map((item) => {
              const count = item.value === 'all'
                ? records.length
                : records.filter(r => r.source === item.value).length;
              return (
                <button
                  key={item.value}
                  onClick={() => setSourceFilter(item.value as typeof sourceFilter)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    sourceFilter === item.value
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  {item.label}（{count}）
                </button>
              );
            })}
          </div>
        )}

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        ) : filteredRecords.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center"
          >
            <BookOpen className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <p className="text-gray-500 mb-2">暂无答题记录</p>
            <p className="text-sm text-gray-400 mb-4">快去刷题吧！</p>
            <button
              onClick={() => navigate('/exam-platform')}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              去刷题
            </button>
          </motion.div>
        ) : (
          <div className="space-y-3">
            {filteredRecords.map((record, index) => {
              // 分项得分 — 直接使用数据库保存的值
              const programmingTypes = ['scratch_coding', 'python_coding', 'cpp_coding'];
              const hasProgrammingQuestions = record.questions?.some((q: any) => programmingTypes.includes(q.type)) ?? false;
              const programmingGraded = record.programmingScore !== undefined && record.programmingScore !== null;

              // 新增：判断是否应该显示"待批阅"
              const isAssignmentSource = record.source === 'class' || record.source === 'exam'; // 来自作业
              const shouldShowPending = isAssignmentSource && !programmingGraded; // 只有作业且未批阅才显示"待批阅"

              // 显示值
              const displayObjectiveScore = record.objectiveScore ?? 0;
              const displayProgrammingScore: number | string = programmingGraded
                ? (record.programmingScore as number)
                : (shouldShowPending ? '待批阅' : '-');
              const displayTotalScore: number | string = (hasProgrammingQuestions && !programmingGraded)
                ? '-'
                : record.score;

              const timeUsed = record.exam_duration - record.time_left;
              return (
                <motion.div
                  key={record.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 hover:shadow-md transition-shadow"
                >
                  {/* 卡片头部：试卷标题和作答次数 */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 text-base leading-tight truncate pr-4 flex items-center gap-2">
                        {/* 来源标签 - 更醒目的设计 */}
                        {record.sourceLabel && (
                          <span className={`px-2.5 py-1 rounded-md text-xs font-bold tracking-wide uppercase whitespace-nowrap ${getSourceBadgeStyle(record.source)}`}>
                            {record.sourceLabel}
                          </span>
                        )}
                        <span className="truncate">{record.paper_title}</span>
                      </h3>
                    </div>
                    <span className="px-2.5 py-1 bg-blue-50 text-blue-600 rounded-full text-xs font-medium whitespace-nowrap">
                      {getSubmitCountText(record.submit_count)}
                    </span>
                  </div>

                  {/* 分项得分区域 */}
                  <div className="flex items-center gap-2 mb-4">
                    <div className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border bg-blue-50 border-blue-200">
                      <span className="text-blue-600 text-xs">客观题</span>
                      <span className="text-blue-700 font-bold text-sm">
                        {displayObjectiveScore === 0 || displayObjectiveScore === null || displayObjectiveScore === undefined ? '-' : `${displayObjectiveScore}分`}
                      </span>
                    </div>
                    {hasProgrammingQuestions && (
                      <div className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg border ${
                        displayProgrammingScore === '待批阅'
                          ? 'bg-amber-50 border-amber-200'
                          : 'bg-purple-50 border-purple-200'
                      }`}>
                        <span className={`text-xs ${
                          displayProgrammingScore === '待批阅'
                            ? 'text-amber-600'
                            : 'text-purple-600'
                        }`}>编程题</span>
                        <span className={`font-bold text-sm ${
                          displayProgrammingScore === '待批阅'
                            ? 'text-amber-600'
                            : 'text-purple-700'
                        }`}>
                          {displayProgrammingScore === '待批阅' ? '待批阅' : (displayProgrammingScore === 0 || displayProgrammingScore === null || displayProgrammingScore === undefined || displayProgrammingScore === '-' ? '-' : `${displayProgrammingScore}分`)}
                        </span>
                      </div>
                    )}
                    <div className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg border ${
                      displayTotalScore === '-' ? 'bg-gray-50 border-gray-200' : 'bg-red-50 border-red-200'
                    }`}>
                      <span className={`text-xs ${
                        displayTotalScore === '-' ? 'text-gray-400' : 'text-red-600'
                      }`}>总分</span>
                      <span className={`font-bold text-sm ${
                        displayTotalScore === '-' ? 'text-gray-400' : 'text-red-600'
                      }`}>
                        {displayTotalScore === '-' || displayTotalScore === 0 || displayTotalScore === null || displayTotalScore === undefined ? '-' : `${displayTotalScore}分`}
                      </span>
                    </div>
                  </div>

                  {/* 底部信息：时间和操作按钮 */}
                  <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                    <div className="flex items-center gap-1 text-xs text-gray-400">
                      <Calendar className="w-3.5 h-3.5" />
                      <span>{formatDateTime(record.created_at)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => viewDetail(record)}
                        className="flex items-center gap-1 px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors font-medium"
                      >
                        <Eye className="w-4 h-4" />
                        查看详情
                      </button>
                      <button
                        onClick={() => {
                          if (!record.paper_exists) {
                            alert('后台原卷已删除，无法作答！');
                            return;
                          }
                          handleRetake(record);
                        }}
                        disabled={checkingPermission === record.id}
                        className={`flex items-center gap-1 px-4 py-2 text-sm font-medium rounded-lg transition-all shadow-sm ${
                          checkingPermission === record.id
                            ? 'bg-gray-300 text-white cursor-not-allowed'
                            : !record.paper_exists
                              ? 'bg-gray-300 text-white cursor-not-allowed'
                              : 'bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600 hover:shadow-md'
                        }`}
                      >
                        {checkingPermission === record.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <RotateCcw className="w-4 h-4" />
                        )}
                        {checkingPermission === record.id ? '检查中...' : '重新作答'}
                      </button>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      <AnimatePresence>
        {showDetailModal && selectedRecord && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl p-6 w-full max-w-md"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-800">答题详情</h3>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="font-medium text-gray-800">{selectedRecord.paper_title}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">
                      {getSubmitCountText(selectedRecord.submit_count)}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-blue-50 rounded-lg p-3 text-center">
                    <p className="text-sm text-gray-500">得分</p>
                    <p className="text-xl font-bold text-blue-600">{selectedRecord.score}/{selectedRecord.total_score}</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-3 text-center">
                    <p className="text-sm text-gray-500">正确率</p>
                    <p className="text-xl font-bold text-green-600">
                      {getCorrectRate(selectedRecord.score, selectedRecord.total_score)}%
                    </p>
                  </div>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">用时</span>
                    <span className="text-gray-800">{formatDuration(selectedRecord.exam_duration - selectedRecord.time_left)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">提交时间</span>
                    <span className="text-gray-800">{formatDateTime(selectedRecord.created_at)}</span>
                  </div>
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => {
                      if (!selectedRecord.paper_exists) {
                        alert('后台原卷已删除，无法作答！');
                        return;
                      }
                      setShowDetailModal(false);
                      handleRetake(selectedRecord);
                    }}
                    className={`flex-1 py-2.5 rounded-lg transition-colors ${
                      !selectedRecord.paper_exists
                        ? 'bg-gray-300 text-white cursor-not-allowed'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    重新作答
                  </button>
                  <button
                    onClick={() => setShowDetailModal(false)}
                    className="flex-1 py-2.5 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    关闭
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 购买刷题包弹窗 */}
      {showPurchaseModal && selectedPaperForPurchase && (
        <PackagePurchaseModal
          paper={selectedPaperForPurchase}
          onClose={() => {
            setShowPurchaseModal(false);
            setSelectedPaperForPurchase(null);
          }}
        />
      )}
    </div>
  );
}
